import React, { useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { ReactComponent as DownloadIcon } from "../../assets/images/download-icon.svg";
import { ReactComponent as PercentIcon } from "../../assets/images/percentage-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import { ReactComponent as ToggleBarIcon } from "../../assets/images/toggle-arrow.svg";
import CButton from "../../components/CButton";
import CDataGrid from "../../components/CDataGrid";
import CRadioGroup from "../../components/CRadioGroup";
import CTextField from "../../components/CTextField";
import CDialog from "../../components/CDialog";
import CloseIcon from "@mui/icons-material/Close";
import { postBackendHelperContent } from "../../utils/backendHelperApi";
import {
  dataTransformationPrompt,
  generateTrainingPlanPrompt,
} from "../../constant/prompt";
import {
  formatZAR,
  getLocalData,
} from "../../constant/utils/commonUtils";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import {
  jobType,
  roleType,
  statuses,
  statusType,
} from "../../constant/constants";
import { fromWithCompany, supabase } from "../../config/supabaseClient";
import { fetchYearId } from "../../slices/yearSlice";
import { fetchBBBEEBudgetConfiguration } from "../../slices/b_bbeeConfigurationSlice";
import {
  addHistoryData,
  addPrograms,
  addSummary,
  deleteHistoryData,
  trainingHistory,
} from "../../slices/trainingProgrammeSlice";
import { toast } from "react-toastify";
import { messages } from "../../constant/alertMessages";
import * as XLSX from "xlsx";

const GenerateTraining = () => {
  const [columns, setColumns] = useState([]);
  const dispatch = useDispatch();
  const { setaTemplate, history, summary, allocatedPrograms, isWorkflowReq } =
    useSelector((store) => ({
      setaTemplate: store.setaTemplate.setaTemplate,
      history: store.trainingProgramme.history,
      summary: store.trainingProgramme.summary,
      allocatedPrograms: store.trainingProgramme.allocatedPrograms,
      isWorkflowReq: store.setaTemplate.isWorkflowReq,
    }));

  const [loading, setLoading] = useState(false);

  const [yearId, setYearId] = useState(null);
  const [totalBudget, setTotalBudget] = useState(0);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  useEffect(() => {
    if (setaTemplate?.length) return;
    dispatch(fetchSetaTemplateByCompanyId(getLocalData().company_id));
  }, []);

  const [data, setData] = useState({
    company_strategy: "",
    "Department Wise Config": [],
    "Demographic Wise Config": [],
    "Training Type Wise Config": [],
    employeeDetails: [],
    trainings: [],
  });

  // Fetch functions
  const fetchLatestFile = async () => {
    try {
      const { data: latestFile, error } = await supabase
        .from("strategic_input")
        .select("*")
        .eq("company_id", getLocalData()?.company_id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching latest file:", error);
        toast.error("Error fetching strategic input: " + error.message);
        return;
      }

      if (latestFile && latestFile.strategic_input_summary) {
        setData((prevData) => ({
          ...prevData,
          company_strategy: latestFile.strategic_input_summary,
        }));
      }
    } catch (error) {
      console.error("Error in fetchLatestFile:", error);
      toast.error("Error in fetchLatestFile: " + error.message);
    }
  };

  const fetchConfiguration = async () => {
    try {
      const { data: configData, error } = await fromWithCompany(
        "configurations"
      )
        .select("*")
        .eq("year_id", yearId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching configuration:", error);
        toast.error("Error fetching configuration: " + error.message);
        return;
      }

      if (!configData) {
        return;
      }

      setData((prevData) => ({
        ...prevData,
        "Department Wise Config": configData.department_config || [],
        "Training Type Wise Config":
          configData.training_category_budget_config || [],
        "Demographic Wise Config": configData.demographic_config?.config || [],
      }));
      setTotalBudget(configData.total_budget || 0);
    } catch (error) {
      console.error("Error in fetchConfiguration:", error);
      toast.error("Error in fetchConfiguration: " + error.message);
    }
  };
  const fetchEmployeeDetails = async () => {
    try {
      const { data: employeeData, error } = await supabase
        .rpc("get_company_training_data", {
          p_company_id: getLocalData()?.company_id,
        })
        .single();

      if (error && error.code !== "PGRST116") {
        toast.error("Error fetching employee details: " + error.message);
        return;
      }

      setData((prevData) => ({
        ...prevData,
        employeeDetails: employeeData,
      }));
    } catch (error) {
      console.error("Error in fetchEmployeeDetails:", error);
      toast.error("Error in fetchEmployeeDetails: " + error.message);
    }
  };

  const fetchTrainings = async () => {
    try {
      const { data: trainingData, error } = await supabase
        .from("training_catalogue_data")
        .select("*")
        .or(`company_id.eq.${getLocalData()?.company_id},company_id.is.null`)
        .eq("status", statuses.ACTIVE);

      if (error) {
        console.error("Error fetching trainings:", error);
        toast.error("Error fetching trainings: " + error.message);
        return;
      }

      if (trainingData && Array.isArray(trainingData)) {
        setData((prevData) => ({
          ...prevData,
          trainings: trainingData,
        }));
      } else {
        setData((prevData) => ({
          ...prevData,
          trainings: [],
        }));
      }
    } catch (error) {
      console.error("Error in fetchTrainings:", error);
      toast.error("Error in fetchTrainings: " + error.message);
    }
  };

  const [selectedHistory, setSelectedHistory] = useState(null);

  useEffect(() => {
    if (!history.length) return;
    handleLoadData(history[0]);
    setSelectedHistory(history[0].id);
  }, [history]);

  useEffect(() => {
    dispatch(fetchYearId()).then((res) => setYearId(res.payload));
    fetchLatestFile();
    fetchEmployeeDetails();
    fetchTrainings();
    dispatch(trainingHistory(getLocalData()?.company_id));
  }, []);

  useEffect(() => {
    if (yearId) fetchConfiguration();
  }, [yearId]);

  useEffect(() => {
    let newColumns = [];
    for (let field of setaTemplate) {
      if (field?.config?.isTrainingCostField) {
        // setTrainingCostFieldName(field.fieldName);
      }
      if (field.config.generatePlan)
        newColumns.push({
          header: field.fieldName,
          field: field.fieldName,
          minWidth: field.fieldName.length > 10 ? 200 : 100,
          id: Date.now(),
          renderCell: ({ row }) => {
            const value = row[field.fieldName];

            if (Array.isArray(value)) {
              return value.length ? value.join(", ") : "-";
            }
            return value || "-";
          },
        });
    }
    setColumns(newColumns);
  }, [setaTemplate]);

  const updateHistory = async (historyData, budgetUsed) => {
    try {
      const { data: updateHistoryData, error: updateHistoryError } =
        await supabase
          .from("batch_jobs")
          .update({
            status: statusType.COMPLETED,
            processed_records: data.employeeDetails.length,
            input_data: {
              ...historyData.input_data,
              budgetUsed: budgetUsed,
            },
          })
          .eq("id", historyData.id)
          .select("*")
          .single();
      if (updateHistoryError && updateHistoryError.code !== "PGRST116") {
        console.error("Error in handleGeneratePlan:", updateHistoryError);
        return;
      }
      dispatch(addHistoryData(updateHistoryData));
      dispatch(addSummary(updateHistoryData.input_data));
    } catch (error) {
      console.error("Error in updateHistory:", error);
    }
  };

  const addAllocatedData = async (data, batchJobsId) => {
    try {
      const { data: allocatedData, error: allocatedDataError } = await supabase
        .from("allocated_programmes")
        .insert(
          data.map((item) => ({ data: item, batch_jobs_id: batchJobsId }))
        )
        .select("*");

      if (allocatedDataError) {
        console.error("Error in addAllocatedData:", allocatedDataError);
        return;
      }
      toast.success("Data added successfully");
    } catch (error) {
      console.error("Error in addAllocatedData:", error);
      toast.error("Error in addAllocatedData: " + error.message);
    }
  };

  const handleGeneratePlan = async () => {
    try {
      setLoading(true);
      dispatch(addPrograms([]));
      dispatch(addSummary({}));
      const missingData = [];

      if (!data.company_strategy) missingData.push("Company Strategy");
      if (!data.employeeDetails.length) missingData.push("Employee Details");
      if (!data.trainings.length) missingData.push("Training Programs");
      if (!setaTemplate.length) missingData.push("SETA Template");
      if (!data["Department Wise Config"].length)
        missingData.push("Department Configuration");
      if (!data["Training Type Wise Config"].length)
        missingData.push("Training Type Configuration");
      if (!data["Demographic Wise Config"].length)
        missingData.push("Demographic Configuration");

      if (missingData.length > 0) {
        toast.error(`Missing required data: ${missingData.join(", ")}`);
        return;
      }
      let localData = getLocalData();
      const { data: historyData, error: historyError } = await supabase
        .from("batch_jobs")
        .insert({
          company_id: localData?.company_id,
          created_by: localData?.id,
          input_data: {
            totalBudget,
            budgetUsed: 0,
          },
          status: statusType.PROCESSING,
          total_records: data.employeeDetails.length,
          processed_records: 0,
          job_type: jobType.TRAINING,
          year_id: yearId,
        })
        .select("*")
        .single();
      if (historyError && historyError.code !== "PGRST116") {
        console.error("Error in handleGeneratePlan:", historyError);
        return;
      }
      dispatch(addHistoryData(historyData));
      setSelectedHistory(historyData.id);
      const content = await postBackendHelperContent({
        prompt: generateTrainingPlanPrompt,
        message: { ...data, setaTemplate },
        llmProvider: "openai",
      });
      const res =
        typeof content === "string" ? JSON.parse(content) : content;
      dispatch(
        addPrograms(res.data.map((item, index) => ({ ...item, id: index })))
      );
      addAllocatedData(res.data, historyData.id);
      updateHistory(historyData, res.budgetUsed);
    } catch (error) {
      console.error("Error in handleGeneratePlan:", error);
      toast.error("Error in handleGeneratePlan: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (item) => {
    const { data: deleteHistoryTable, error: deleteHistoryTableError } =
      await supabase.from("batch_jobs").delete().eq("id", item.id);
    if (deleteHistoryTableError) {
      if (deleteHistoryTableError.code === "23503") {
        return toast.error(
          "Cannot delete this batch job because it has related records (e.g. employees, allocated programmes, or logs). Remove or unlink related data first."
        );
      }
      return toast.error(messages.SERVER_ERROR);
    }
    dispatch(addPrograms([]));
    dispatch(addSummary({}));

    toast.success(messages.DELETED_SUCCESSFULLY);

    dispatch(deleteHistoryData(item));
  };

  const handleLoadData = async (item) => {
    try {
      const { data, error } = await supabase
        .from("allocated_programmes")
        .select("data, id")
        .eq("batch_jobs_id", item.id);

      if (error) {
        toast.error(messages.SERVER_ERROR);
        return;
      }

      dispatch(
        addPrograms(data.map((item) => ({ ...item.data, id: item.id })))
      );
      setSelectedHistory(item.id);

      // let summary = {
      //   ...item.input_data,
      //   ...findSummary(rowData, item),
      // };
      dispatch(addSummary(item.input_data));
    } catch (error) {
      console.error("Error in handleLoadData:", error);
      toast.error(messages.SERVER_ERROR);
    }
  };

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const outputRequiredFields = setaTemplate
      ?.filter((field) =>
        isWorkflowReq
          ? field?.config?.exportSheet
          : field?.config?.exportSheet && !field?.config?.isDepartmentNameField
      )
      .map((field) => field.fieldName);

    const filteredData = allocatedPrograms?.map((record) => {
      const entry = record;
      const filteredEntry = {};

      outputRequiredFields.forEach((field) => {
        filteredEntry[field] = entry[field];
      });

      return filteredEntry;
    });
    const ws = XLSX.utils.json_to_sheet(filteredData);

    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    XLSX.writeFile(wb, `Allocated Programs-${Date.now()}.xlsx`);
  };

  return (
    <div className="flex items-center justify-between flex-wrap h-[calc(100vh-68px)] relative">
      {/* Chat AI */}
      <div
        className={`h-full border-r border-solid border-grey-400 transition-all duration-500 ease-in-out overflow-hidden ${
          isAISidebarOpen
            ? "w-[600px] opacity-100"
            : "w-0 border-none opacity-0"
        }`}
      >
        <div className="grid grid-cols-1 grid-rows-[60px_auto] h-full">
          <div className="flex justify-between items-center py-1 border-b border-solid border-grey-300 px-3">
            <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-blue-500">
              Generated Training Plan History
            </p>

            <CButton onClick={() => setIsAISidebarOpen(false)} className="!p-0">
              <ToggleBarIcon className="w-[24px] h-[24px] !stroke-blue-500" />
            </CButton>
          </div>
          <div className="p-4">
            <div className="overflow-y-auto h-full max-h-[calc(100vh-164px)] rounded-lg border border-solid border-blue-200">
              <table className="w-full border-collapse custom-table ">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Date
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Summary
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      Status
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2">
                      <Tooltip
                        title="Processed Employees/Total Employees"
                        placement="top"
                        arrow
                      >
                        <span>R/T</span>
                      </Tooltip>
                    </th>
                    <th className="text-left border-b border-solid border-blue-200 font-semibold text-[13px] leading-sm bg-blue-100 p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {history && history.length > 0 ? (
                    history.map((item) => {
                      return (
                        <tr
                          key={item.id}
                          className={`border-b border-solid border-blue-200 ${
                            selectedHistory === item.id ? "bg-blue-50" : ""
                          }`}
                        >
                          <td
                            className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-1 w-[170px] text-gray-500 cursor-pointer"
                            onClick={() => handleLoadData(item)}
                          >
                            {moment(item.created_at).format(
                              "DD-MM-YYYY hh:MM A"
                            )}
                          </td>
                          <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-1 text-gray-500">
                            <p className="relative">
                              Total budget:
                              <span className="mx-1 text-blue-500 font-medium">
                                {formatZAR(item?.input_data?.totalBudget)}
                              </span>
                            </p>
                            <p>
                              Budget Used:
                              <span className="mx-1 text-blue-500 font-medium">
                                {formatZAR(item?.input_data?.budgetUsed)}
                              </span>
                            </p>
                            {/* <p>
                            Allocation type:
                            <span className="mx-1 text-blue-500 font-medium capitalize">
                              {item?.input_data?.allocateBasedOn}
                            </span>
                          </p> */}
                          </td>
                          <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-2 text-gray-500">
                            <p className=" relative capitalize">
                              {item?.status}
                            </p>
                          </td>
                          <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-2 text-gray-500">
                            <p className="relative">
                              {item?.processed_records}/{item?.total_records}
                            </p>
                          </td>
                          <td className="text-left border-b border-solid border-blue-200 text-[13px] leading-sm p-2 text-gray-500 ">
                            <CButton
                              onClick={() => {
                                handleDeleteHistory(item);
                              }}
                              className="!min-w-0 bg-red-100 text-red-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
                            >
                              <DeleteIcon />
                            </CButton>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="text-center border-b border-solid border-blue-200 text-[13px] leading-sm p-4 text-gray-500"
                      >
                        No history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <CDialog open={false}>
          <CButton className="absolute right-0 top-0">
            <CloseIcon />
          </CButton>
          <div className="p-3">
            <div className="flex justify-between items-center">
              <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-grey-500">
                Budget Allocation:
              </p>
            </div>
            <div className="custom-input-group">
              <div className="flex items-center">
                <CTextField
                  name="totalBudget"
                  label="Total Budget:"
                  className="budget-inputs !my-3"
                  value=""
                  autoComplete="off"
                />
                <span className="text-blue-500 font-semibold mx-2">R</span>
              </div>
              <CRadioGroup
                name="radioOption"
                label="Select an Option:"
                row
                className="budget-inputs"
                value=""
                spacing={2}
                options={[
                  { value: "equal", label: "Equal Allocation" },
                  { value: "race", label: "Race" },
                  { value: "gender", label: "Gender" },
                ]}
              />
              <div className="flex items-center gap-1 flex-wrap mt-2">
                <p className="text-xs leading-xs font-normal text-blue-500 bg-blue-100 rounded-2xl px-3 py-1">
                  Total Employees <span className="font-semibold">0</span>
                </p>
              </div>
              <div className="flex items-center gap-1 flex-wrap mt-2">
                <p className="text-xs leading-xs font-normal text-blue-500 bg-blue-100 rounded-2xl  px-3 py-1 capitalize">
                  male <span className="font-semibold">0</span>
                </p>
              </div>
              <div className="budget-inner-inputs">
                <p className="text-sm leading-sm text-blue-500 font-medium mt-2 capitalize">
                  equal Based Allocation: 100.00%
                </p>
                <div className="flex items-center gap-2">
                  <CTextField
                    name="male"
                    label="male"
                    value=""
                    autoComplete="off"
                    className="!2xl:mt-2 !mt-2 capitalize"
                    endIcon={<PercentIcon className="fill-blue-300" />}
                  />
                </div>
              </div>

              <div className="flex justify-center mt-3">
                <CButton
                  className={`chat-ai-outlined-btn !h-[40px] text-sm leading-sm ${
                    isAISidebarOpen ? "hidden" : ""
                  }`}
                  onClick={() => setIsAISidebarOpen(true)}
                >
                  <span className="ai-gradient-text">Generate Plan</span>
                </CButton>
              </div>
            </div>
          </div>
        </CDialog>
      </div>

      <div
        className={`h-full transition-[width] duration-500 ease-in-out 2xl:px-4 px-3 ${
          isAISidebarOpen ? "w-[calc(100%-600px)]" : "w-full"
        }`}
      >
        <div className="bg-white border border-solid border-blue-200 border-t-0 border-b-0  overflow-hidden">
          <div className="flex items-center justify-between p-2">
            <div className="flex gap-2 items-center">
              {!isAISidebarOpen && (
                <CButton
                  className="!p-0 mr-2"
                  onClick={() => setIsAISidebarOpen(true)}
                  type="button"
                >
                  <ToggleBarIcon className="w-[24px] h-[24px] !stroke-blue-500 rotate-180" />
                </CButton>
              )}
              <CTextField
                name="search"
                value=""
                startIcon={<SearchIcon className="text-blue-300" />}
                placeholder={"Search"}
                className="!mt-0 w-full 2xl:max-w-[300px] max-w-[200px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <CButton
                className={`gradient-btn ${true ? "block" : "hidden"}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleGeneratePlan();
                }}
              >
                Generate Plan
              </CButton>
              <CButton
                className=" text-blue-500 hover:text-white hover:bg-blue-500 border border-solid border-blue-500 rounded-lg p-0  inline-flex items-center gap-1 group disabled:opacity-50"
                disabled={!allocatedPrograms?.length}
                onClick={handleExport}
              >
                <DownloadIcon className="fill-blue-500 group-hover:fill-white" />
                WSP File
              </CButton>
              <CButton className={`gradient-btn ${true ? "hidden" : ""}`}>
                Generate Plan
              </CButton>
            </div>
          </div>

          <div className="h-[calc(100vh-134px)] w-full relative gen-training-table">
            <CDataGrid
              searchQuery=""
              rows={allocatedPrograms}
              columns={columns || []}
              page={0}
              rowsPerPage={25}
              rowsPerPageOptions={[25, 50, 100]}
              loading={loading}
              idKey="id"
              columnVisibilityModel={{
                id: false,
              }}
              className="bg-white shadow-md rounded-lg"
            />

            <div className="absolute bottom-[6px] left-[12px] h-[70px] w-[calc(100%-384px)]">
              <div className="flex gap-x-2 items-center flex-wrap">
                <p className="text-gray-700  font-semibold text-sm leading-sm w-full">
                  Total Budget Used:
                  <span className="text-blue-500 font-semibold mx-1">
                    {formatZAR(summary?.budgetUsed)}
                  </span>
                </p>
                {/* <p className="text-gray-500 text-sm leading-sm  my-1 capitalize">
                  male:
                  <span className="text-blue-500 font-semibold mx-1">R 0</span>
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateTraining;
